import { SENTRY_DSN } from "@acme/shared/create-router";
import { DevtoolsComponent } from "@acme/shared/dev-tools";
import { Toaster } from "@acme/ui/sonner";
import manropeFontUrl from "@fontsource-variable/manrope/files/manrope-latin-wght-normal.woff2?url";
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  ScriptOnce,
  Scripts,
} from "@tanstack/react-router";
import { MotionConfig } from "framer-motion";
import { analyticsResourceHints } from "~/components/consent-manager/resource-hints";
import { ThirdPartyLayer } from "~/components/consent-manager/third-party-layer";
import { getLocale, getTextDirection } from "~/paraglide/runtime.js";
import appCss from "~/styles.css?url";

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  head: ({ matches }) => ({
    links: [
      {
        as: "font",
        crossOrigin: "anonymous",
        fetchPriority: "high",
        href: manropeFontUrl,
        rel: "preload",
        type: "font/woff2",
      },
      ...analyticsResourceHints(matches),
      // Sentry ingest gets a session envelope on hydration, but it is not
      // render-critical, so it does not earn a preconnect socket.
      { href: new URL(SENTRY_DSN).origin, rel: "dns-prefetch" },
      // The homepage hero photograph is the LCP element and is served from
      // Pexels, so the connection is worth opening during head parse.
      { href: "https://images.pexels.com", rel: "preconnect" },
      { href: "https://images.pexels.com", rel: "dns-prefetch" },
      { as: "style", href: appCss, rel: "stylesheet", type: "text/css" },
      { href: "/favicon.ico", rel: "icon", sizes: "48x48" },
      { href: "/images/icon.svg", rel: "icon", type: "image/svg+xml" },
      { href: "/apple-touch-icon.png", rel: "apple-touch-icon" },
      { href: "/site.webmanifest", rel: "manifest" },
    ],
    meta: [
      {
        charSet: "utf-8",
      },
      {
        content: "width=device-width, initial-scale=1",
        name: "viewport",
      },
      // The paper ground of the design. Not media-keyed: the site ships a
      // single light rendition, so this always matches the paint.
      {
        // A meta tag cannot read a CSS custom property, so this literal has to
        // mirror --ld-paper by hand. token-lint-allow
        content: "#fbfaf7",
        name: "theme-color",
      },
    ],
  }),
  shellComponent: ({ children }) => <ShellComponent>{children}</ShellComponent>,
  staleTime: Number.POSITIVE_INFINITY,
});

/**
 * Sets `data-skin` on <html> from the `demo_skin` cookie before first paint.
 * Kept out of the SSR markup so public route responses stay request-independent
 * and safe to CDN-cache (the showcase skin must not bleed across visitors).
 * Mirrors the theme detector script in the shared theme provider.
 */
const skinDetectorScript = (() => {
  // Serialized to a standalone inline script, so it must be fully self-contained
  // (no module-scope references, no regex literal hoisting).
  function skinFn() {
    try {
      const name = "demo_skin=";
      let value = "modern";
      for (const part of document.cookie.split("; ")) {
        if (part.startsWith(name)) {
          value =
            part.slice(name.length) === "heritage" ? "heritage" : "modern";
          break;
        }
      }
      document.documentElement.dataset.skin = value;
    } catch {
      document.documentElement.dataset.skin = "modern";
    }
  }
  return `(${skinFn.toString()})();`;
})();

function ShellComponent({ children }: { readonly children: React.ReactNode }) {
  return (
    // data-skin is a constant default here (request-independent, so cacheable);
    // skinDetectorScript sets the real per-visitor skin before first paint.
    // suppressHydrationWarning: the skin script mutates <html> pre-hydration.
    // No theme class ships: the site is light-only, and the paper ground comes
    // from the token layer rather than a class on the root element.
    <html
      className="scroll-smooth"
      data-skin="modern"
      dir={getTextDirection()}
      lang={getLocale()}
      suppressHydrationWarning
    >
      <head>
        {/** biome-ignore lint/correctness/noChildrenProp: ScriptOnce API */}
        <ScriptOnce children={skinDetectorScript} />
        <HeadContent />
      </head>
      <body className="min-h-screen bg-background font-sans text-foreground antialiased">
        {/* framer-motion ignores prefers-reduced-motion unless told to honour
            it, and the global CSS reduced-motion rule only reaches CSS
            animations — motion writes inline styles. Both layers are needed. */}
        <MotionConfig reducedMotion="user">
          <ThirdPartyLayer>
            {children}
            <Toaster resolvedTheme="light" />
          </ThirdPartyLayer>
        </MotionConfig>

        {import.meta.env.DEV ? <DevtoolsComponent /> : null}
        <Scripts />
      </body>
    </html>
  );
}
